import { apiClient } from '@/lib/api/client'
import { create } from 'zustand'
import { useSessionStore } from './sessionStore'

/**
 * Auth client state — GDEV-54 실 연동.
 *
 * 실제 인증은 HttpOnly Cookie + JWT(Backend) 로 처리.
 * 이 스토어는 **표시용 프로필/역할** 만 보관한다 (토큰 저장 금지).
 *
 * 세션 복원: 앱 부팅 시 `fetchMe()` 호출 → Cookie 기반 GET /auth/me → user 설정.
 */

export interface AuthUser {
  id: string
  email: string | null
  displayName: string | null
  hfUsername: string | null
  /** Backend `/auth/me` 의 `hf_organization` — 소속 HF 조직 (예: etri-humanoidrobotsystems). */
  hfOrganization: string | null
  role: string
  isActive: boolean
  registrationStatus?: 'pending' | 'approved' | 'rejected'
  registrationReviewReason?: string | null
  /**
   * 권한매트릭스 v0.2 의 2D 4분류 중 `is_org_member` 차원.
   *
   * - `'member'` — HF Org 멤버 (etri 멤버, 큐레이션·댓글·신고 권한 부여)
   * - `'none'`   — 외부 일반 사용자 (조회·다운로드만)
   *
   * 5/14 GDEV-231 시점 backend `/auth/me` 응답의 `hf_org_membership_status`.
   * admin / researcher 는 정의상 항상 `'member'` (5/8 §4.3 invariant).
   */
  hfOrgMembershipStatus: 'member' | 'none'
}

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  setUser: (user: AuthUser | null) => void
  fetchMe: () => Promise<void>
  logout: () => Promise<void>
}

/**
 * 권한매트릭스 v0.2 의 `is_org_member` 차원 helper — admin/researcher 는
 * admin 은 항상 멤버로 본다. researcher 와 user 는 실제 조직 멤버십
 * (`hfOrgMembershipStatus === 'member'`) 을 확인한다.
 *
 * Community 게시·댓글·신고, Curation 생성 등 5/11 §4 의 "etri 멤버만" 분기에
 * 사용한다.
 */
export function isOrgMember(user: AuthUser | null): boolean {
  if (!user) return false
  // admin 은 부트스트랩 allowlist 로 조직 멤버십과 무관하게 부여되는 경로가
  // 설계상 존재한다. researcher 는 조직 멤버여야 한다 — 서버가 부여 시점과
  // 로그인 시점에 강제하며, 화면도 같은 규칙을 따라야 쓰기 UI 가 열렸다가
  // API 에서 403 으로 막히는 어긋남이 생기지 않는다 (QA H-11).
  if (user.role === 'admin') return true
  return user.hfOrgMembershipStatus === 'member'
}

/**
 * 데이터셋 · 모델을 새로 등록할 수 있는가 — 백엔드 `dataset:create` · `model:create`
 * 와 같은 규칙: admin, 또는 조직 멤버인 researcher (권한매트릭스 v0.2 4.1 개정, H-18).
 *
 * 화면만 admin 으로 막혀 있어 연구자가 등록 화면에 들어가지 못했다
 * (2026-10-13 발주처 모델 기능 점검 ①).
 */
export function canCreateContent(user: AuthUser | null): boolean {
  if (!user) return false
  if (user.role === 'admin') return true
  return user.role === 'researcher' && isOrgMember(user)
}

/**
 * HF 소스(huggingface.co) 직접 링크 노출 게이트 — admin/researcher 만.
 *
 * 플랫폼은 다운로드/조회/프리뷰/토론을 BE 프록시로 on-platform 제공하므로 일반
 * 사용자는 HF.co 로 이탈할 필요가 없다. HF 원본 repo 링크("HF Hub ↗")는 디버깅·검증
 * 용도라 staff(admin/researcher)에게만 노출하고, 일반 사용자는 플랫폼에 머물게 한다.
 * (2026-06-06 결정. ETRI GDEV-222 의 커뮤니티 "링크만 표시" 와는 별개 — 그쪽은 유지.)
 */
export function isStaff(user: AuthUser | null): boolean {
  return !!user && (user.role === 'admin' || user.role === 'researcher')
}

/** Backend /auth/me 응답을 AuthUser 로 매핑. */
function mapProfile(data: Record<string, unknown>): AuthUser {
  const membershipRaw = data.hf_org_membership_status
  const hfOrgMembershipStatus: 'member' | 'none' = membershipRaw === 'member' ? 'member' : 'none'
  return {
    id: String(data.id ?? ''),
    email: (data.email as string) ?? null,
    displayName: (data.display_name as string) ?? null,
    hfUsername: (data.hf_username as string) ?? null,
    hfOrganization: (data.hf_organization as string) ?? null,
    role: String(data.role ?? 'user'),
    isActive: data.is_active !== false,
    registrationStatus:
      data.registration_status === 'pending' || data.registration_status === 'rejected'
        ? data.registration_status
        : 'approved',
    registrationReviewReason: (data.registration_review_reason as string) ?? null,
    hfOrgMembershipStatus,
  }
}

/** 인증 성공 시 stale session-expired flag 정리. */
function clearSessionExpired() {
  useSessionStore.getState().reset()
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  // 초기 hydration 동안 'loading' 으로 시작 — AdminGuard 가 fetchMe 결과 전에
  // 미인증으로 판정해 홈으로 리다이렉트하는 race 차단 (직접 URL/북마크/F5
  // 진입 시 /admin/* 가 100% 홈으로 튕기던 회귀, /qa 5/22 적출).
  // AppShell 의 useEffect 가 fetchMe() 호출 → 결과에 따라 setUser 또는
  // catch 블록이 isLoading=false 로 전환한다.
  isLoading: true,

  setUser: (user) => {
    if (user !== null) clearSessionExpired()
    set({ user, isAuthenticated: user !== null })
  },

  fetchMe: async () => {
    set({ isLoading: true })
    try {
      const data = await apiClient.get<Record<string, unknown>>('/auth/me')
      const user = mapProfile(data)
      clearSessionExpired()
      set({ user, isAuthenticated: true, isLoading: false })
    } catch {
      // Cookie 없거나 만료 → 미인증 상태
      set({ user: null, isAuthenticated: false, isLoading: false })
    }
  },

  logout: async () => {
    try {
      await apiClient.post('/auth/logout')
    } catch {
      // 서버 에러 무시 — cookie 는 서버가 삭제하지만 클라이언트도 상태 초기화
    }
    set({ user: null, isAuthenticated: false })
  },
}))
